import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileClock } from "lucide-react";
import { WIZARD_KINDS, draftTimeLabel, wizardDraftsApi, type WizardKind } from "@/services/wizardDrafts";
import { useCan } from "@/auth/permissions";

/**
 * WIZDRAFT-1 / WIZDRAFT-2 · 列表页顶部的「没做完的新建 XX」黄条（示意图 ②）：有草稿才出现；各向导的列表页共用。
 * 每行：标题（没起名 =「（还没起名）」）· 做到第 N 步 · 最后存的人 · 时间 · 「继续」「删除」（删除二次确认，页面内确认，不用 confirm()）。
 * 同租户有该向导「新建」权限的人都能看到、能接手别人存的草稿；没权限的角色不显示也不请求。
 * 用法：``<WizardDraftsBar kind="hotel" />``——权限 / 名词 / 「继续」跳回的地址都来自 ``WIZARD_KINDS``（services/wizardDrafts.ts）。
 */
export function WizardDraftsBar({ kind }: { kind: WizardKind }) {
  const cfg = WIZARD_KINDS[kind];
  const canCreate = useCan(cfg.perm);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["wizard-drafts", kind], enabled: canCreate, queryFn: () => wizardDraftsApi.list(kind) });
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => wizardDraftsApi.remove(kind, id),
    onSuccess: () => { setConfirmId(null); qc.invalidateQueries({ queryKey: ["wizard-drafts", kind] }); },
    onError: (e) => alert((e as Error).message || "删除失败"),
  });

  const rows = Array.isArray(q.data) ? q.data : [];   // 接口异常 / 返回不是数组：当没有草稿，黄条绝不拖垮列表页
  if (!canCreate || rows.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900" data-testid="wizard-drafts-bar" data-kind={kind} role="region" aria-label={`没做完的${cfg.noun}`}>
      <div className="flex items-center gap-1.5 text-[13px] font-semibold"><FileClock size={15} /> {rows.length} 个没做完的{cfg.noun}</div>
      <div className="mt-1.5 flex flex-col gap-1.5">
        {rows.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]" data-testid="wizard-draft-row">
            <span className="font-medium">{d.title || "（还没起名）"}</span>
            <span className="text-[12px] text-amber-700/80">{cfg.steps === 1 ? "" : `做到第 ${d.step} 步 · `}{d.updated_by_name ?? d.created_by_name ?? "—"} · {draftTimeLabel(d.updated_at)}</span>
            <span className="ml-auto flex items-center gap-3">
              {confirmId === d.id ? (
                <>
                  <span className="text-[12px] text-rose-700">删掉这份草稿？删了找不回</span>
                  <button type="button" disabled={del.isPending} onClick={() => del.mutate(d.id)} data-testid="wizard-draft-delete-ok" className="text-[12px] font-medium text-rose-700 underline disabled:opacity-50">确认删除</button>
                  <button type="button" onClick={() => setConfirmId(null)} className="text-[12px] underline">取消</button>
                </>
              ) : (
                <>
                  <Link to={`${cfg.newPath}?draft=${d.id}`} className="underline hover:text-amber-700" data-testid="wizard-draft-continue">继续</Link>
                  <button type="button" onClick={() => setConfirmId(d.id)} className="underline hover:text-amber-700" data-testid="wizard-draft-delete">删除</button>
                </>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
